import { request } from './client'

/* ============================================================================
   **CQ-42 — AN ADJUSTMENT IS PROPOSED, AND A SECOND PERSON DECIDES IT.** Sprint 82.

   The client's wording, which CF-08 in the UAT prerequisites was left open against rather
   than guessed at: *"Agreed, all adjustment must be approved by supervisor, and supervisor
   should receive a notification for it."*

   **NOTHING MOVES UNTIL IT IS APPROVED.** That is W-106's shape — a counted shortfall has
   waited for a second person since Sprint 24 — and before this sprint a typed correction
   went straight to the ledger, so one hotel had two answers to one question.

   The movements endpoint now REFUSES an adjustment outright. Anything in this product that
   wants to correct a shelf figure comes here.
   ========================================================================= */

export type AdjustmentState = 'PROPOSED' | 'APPROVED' | 'DECLINED'

export interface StockAdjustmentRow {
  stockAdjustmentId: string
  stockItemId: string
  itemCode: string
  itemName: string
  itemNameAr: string | null
  unit: string
  stockLocationId: string
  locationName: string
  locationNameAr: string | null
  /** **SIGNED.** Plus is "there are more here than you think"; minus is a write-off. */
  quantity: number
  /** What the shelf says TODAY — read at the moment of the query, never snapshotted. A
   *  proposal from Tuesday is judged against Thursday's shelf, and "write off four" is
   *  unanswerable without it. */
  onHandNow: number
  reason: string
  raisedBy: string
  raisedByAr: string | null
  raisedAtUtc: string
  state: AdjustmentState
  decidedBy: string | null
  decidedByAr: string | null
  decidedAtUtc: string | null
  decisionNote: string | null
  /** **WORKED OUT ON THE SERVER.** False on your own proposal. A screen that drew the
   *  buttons and then had the press refused would teach its reader that the product is
   *  unreliable rather than that the rule exists. */
  mayDecide: boolean
}

export interface AdjustmentQueue {
  generatedAtUtc: string
  /** Oldest first: a queue is a queue. */
  waiting: StockAdjustmentRow[]
  /** The last few, newest first — so a row does not simply vanish when it is decided. */
  decided: StockAdjustmentRow[]
}

export const fetchAdjustments = (
  propertyId: string, options: { signal?: AbortSignal } = {}) =>
  request<AdjustmentQueue>(
    `/properties/${propertyId}/stock/adjustments`,
    options.signal ? { signal: options.signal } : {})

export const proposeAdjustment = (
  propertyId: string,
  body: {
    stockItemId: string
    stockLocationId: string
    quantity: number
    reason: string
    capturedAtUtc?: string
  }) =>
  request<{ stockAdjustmentId: string }>(
    `/properties/${propertyId}/stock/adjustments`,
    { method: 'POST', body })

/* TWO CALLS, NOT ONE WITH A BOOLEAN. Approving moves stock and declining demands a note;
   one endpoint taking `approved: false` would put both behind one name in every log. */
export const approveAdjustment = (
  propertyId: string, adjustmentId: string, note?: string) =>
  request<StockAdjustmentRow>(
    `/properties/${propertyId}/stock/adjustments/${adjustmentId}/approve`,
    { method: 'POST', body: { note: note ?? null } })

export const declineAdjustment = (
  propertyId: string, adjustmentId: string, note: string) =>
  request<StockAdjustmentRow>(
    `/properties/${propertyId}/stock/adjustments/${adjustmentId}/decline`,
    { method: 'POST', body: { note } })
